import * as classNames from 'classnames';
import * as React from 'react';
import formatNumber from 'globals/utilities/format-number';
import loadMapsFactory, { Load, Shapes } from 'utilities/load-maps';
import Map from './../map/map';
const countryLookup = makeCountryLookup(require('world-countries') as CountryInformation[]);

interface State {
    countries: CountryAnswer[];
    nextShapes: Shapes | null;
    thisShapes: Shapes;
}

interface Props {
    isRoundInterval: boolean;
    round: Round;
    roundIndex: number;
    rounds: Round[];
    updateResponses: (correct: boolean[]) => void;
}

type CountryLookup = { [index: string]: CountryInformation; };

interface CountryInformation {
    area: number;
    cca3: string;
    name: { common: string; };
}

export default class extends React.Component<Props, State> {

    loadNext: Load;
    metricMaximum: number;
    metricMinimum: number;

    constructor(props: Props) {
        super(props);

        const thisShapes: Shapes = {};
        for (let country of this.props.round.countries) {
            thisShapes[country.countryCode] = country.shape;
        }

        this.state = {
            countries: this.initialise(props),
            thisShapes: thisShapes,
            nextShapes: null
        };

    }

    /**
     * Set up shape loading. This is done one round in advance so the player doesn't have to wait.
     */
    componentDidMount(): void {

        // Get all country codes that will be used in the whole quiz.
        const countryCodesNested = this.props.rounds.map(function (round: Round): string[] {
            return round.countries.map(function (country: Country): string {
                return country.countryCode;
            });
        });

        // Set up shape loading and request the next round's shapes. The first round is already pre-loaded.
        const countryCodes = [].concat.apply(countryCodesNested);
        const loadMaps = loadMapsFactory(countryCodes);
        loadMaps(this.saveMaps.bind(this));

    }

    /**
     * When the player changes round, shapes should be loaded and the next round's shapes should be requested.
     * @param nextProps Properties for the next round or round interval.
     */
    componentWillReceiveProps(nextProps: Props): void {

        if (nextProps.isRoundInterval === false) {

            if (this.state.nextShapes !== null) {

                // The shapes for the next round are ready, so they can be displayed.
                this.setState(
                    {
                        countries: this.initialise(nextProps),
                        thisShapes: this.state.nextShapes,
                        nextShapes: null
                    },
                    () => {
                        if (this.props.roundIndex < this.props.rounds.length - 1) {
                            this.loadNext(this.saveMaps.bind(this));
                        }
                    }
                );

            } else {

                // The next round's shapes are late, so only update the country information.
                this.setState({ countries: this.initialise(nextProps) });

            }

        }

    }

    /**
     * Attach information to each country in the current round including a more readable name and the metric being 
     * tested (i.e. area, population etc.).
     * @param countries List of countries in the current round.
     * @returns         The same list of countries with some extra information.
     */
    formatCountries(countries: Country[]): CountryAnswer[] {

        this.metricMaximum = Number.POSITIVE_INFINITY;
        this.metricMinimum = Number.NEGATIVE_INFINITY;

        return countries.map(function (country: Country, position: number): CountryAnswer {
            const countryInformation = countryLookup[country.countryCode];
            const metricValue = countryInformation[country.metric];
            const metricDisplay = <span>{formatNumber(metricValue)} km&sup2;</span>;
            return {
                ...country,
                metricValue,
                metricDisplay,
                countryName: countryInformation.name.common,
                position,
                initialPosition: position
            };
        });
    }

    /**
     * Prepare answers and responses for the current round.
     * @param props Properties of the current round.
     * @returns     List of countries for the current round.
     */
    initialise(props: Props): CountryAnswer[] {
        const countries = this.formatCountries(props.round.countries);
        this.updateResponses(countries);
        return countries;
    }

    /**
     * Check if two string arrays are equal.
     * @param one First set of items.
     * @param two Second set of items.
     * @returns   True if the array items are equal.
     */
    isEqual(one: number[] | string[], two: number[] | string[]): boolean {

        if (one.length !== two.length) {
            return false;
        }

        let equal = true;
        let index = 0;
        while (index < one.length && equal) {
            equal = one[index] === two[index];
            index++;
        }

        return equal;
    }


    render(): JSX.Element | null {
        const className = classNames('maps', `maps-${this.state.countries.length}`);
        return (
            <div className={className}>
                {this.renderCountries()}
            </div>
        );
    }

    /**
     * Draw a map of each country.
     */
    renderCountries(): JSX.Element[] | null {

        const elements: JSX.Element[] = [];

        this.state.countries.forEach((country: CountryAnswer, index: number): void => {

            const shape = country.shape ? country.shape : this.state.thisShapes[country.countryCode];

            if (index > 0 && this.props.isRoundInterval === false) {
                elements.push(
                    <div
                        key={`swap-${index}`}
                        className="maps-swap"
                    >
                        <div onClick={this.swap.bind(this, index)}>
                            <svg className="icon icon-exchange">
                                <use xlinkHref="#icon-exchange" />
                            </svg>
                        </div>
                    </div>
                );
            }

            elements.push(
                <Map
                    country={country}
                    key={index}
                    index={index}
                    isRoundInterval={this.props.isRoundInterval}
                    roundIndex={this.props.roundIndex}
                    shape={shape}
                />
            );

        });

        return elements;

    }

    /**
     * The shapes for the next round are always requested in advance. However, when they arrive is a mystery. Most 
     * likely they'll be loaded before the player moves to the next round. If they're slow at loading, the played 
     * might have to wait while they load.
     * 
     * @param nextShapes Shapes for the next round.
     * @param loadNext   Function to get the next shapes.
     */
    saveMaps(nextShapes: Shapes, loadNext: Load): void {

        const thisShapesCodes = this.props.round.countries.map(country => country.countryCode).sort();
        const nextShapesCodes = Object.keys(nextShapes).sort();
        if (this.isEqual(thisShapesCodes, nextShapesCodes)) {

            // The shapes have arrived late so they need to be displayed straight away and the next ones requested. 
            this.setState({ thisShapes: nextShapes, nextShapes: null });
            if (this.props.roundIndex < this.props.rounds.length - 1) {
                loadNext(this.saveMaps.bind(this));
            }

        } else {
            this.setState({ nextShapes });
        }

        if (this.props.roundIndex < this.props.rounds.length - 1) {
            this.loadNext = loadNext;
        }

    }

    /**
     * Shapes are loaded in advance of the next round. However, a render shouldn't happen when the next round's 
     * shapes arrive.
     * @param nextProps New properties.
     * @param nextState New state.
     */
    shouldComponentUpdate(nextProps: Props, nextState: State): boolean {

        // If the round is changing or countries were swapped, a render should happen.
        const thisStatePositions = this.state.countries.map(country => country.position);
        const nextStatePositions = nextState.countries.map(country => country.position);
        const stateCodesEqual = this.isEqual(thisStatePositions, nextStatePositions);
        if (!stateCodesEqual || this.props.isRoundInterval !== nextProps.isRoundInterval) {
            return true;
        }

        // If the next shapes are not loaded yet, it means the shapes arrived late so an immediate update is needed.
        if (nextState.nextShapes === null) {
            return true;
        }

        // Try to stop renders when the next shapes were loaded. If the current rounds country codes are the same as 
        // the next shapes' country codes, an update needs to happen because those shapes need to be loaded now.
        const thisRoundCodes = this.props.round.countries.map(country => country.countryCode).sort();
        const nextShapesCodes = Object.keys(nextState.nextShapes).sort();
        return this.isEqual(thisRoundCodes, nextShapesCodes);

    }

    /**
     * Swap two countries.
     * @param index The position to swap.
     */
    swap(index: number): void {
        const countries = this.state.countries.map((country) => ({ ...country }));
        const positions = countries.map(country => country.position);
        const upperIndex = positions.indexOf(index);
        const lowerIndex = positions.indexOf(index - 1);
        countries[upperIndex].position--;
        countries[lowerIndex].position++;
        this.setState({ countries });
        this.updateResponses(countries);
    }

    /**
     * Validate the position of each country and send it to the parent for scoring.
     * @param countries Countries in the order the player has placed them.
     */
    updateResponses(countries: CountryAnswer[]): void {

        // Put the answers in the correct order.
        const answers = countries.map(country => country.metricValue);
        answers.sort((answerOne, answerTwo) => answerOne - answerTwo);

        // Get the responses in the order they are on the screen.
        const responses = countries.slice().sort((one, two) => one.position - two.position);
        const correct = answers.map((answer, answerIndex) => answer === responses[answerIndex].metricValue);
        this.props.updateResponses(correct);

    }

}

function makeCountryLookup(countriesInformation: CountryInformation[]): CountryLookup {
    const result: CountryLookup = {};
    countriesInformation.forEach(function (countryInformation: CountryInformation) {
        result[countryInformation.cca3] = countryInformation;
    });
    return result;
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/maps/maps.tsx